// Per-endpoint LLM target entitlements + just-in-time grants.
//
// Two kinds of answer live behind these endpoints and they are deliberately
// distinct in the UI: a STANDING rule (allow / deny / inherit) that stays until
// someone changes it, and a GRANT that expires by itself. Server-side both are
// collapsed by services/targetAccess.ts into what the endpoint agent enforces.

export type TargetMode = 'allow' | 'deny' | 'inherit';
export type GrantSource = 'admin' | 'request';
export type GrantState = 'active' | 'expired' | 'revoked';
/**
 * How an *allowed* target's traffic reaches the provider — see the firewall's
 * services/targetAccess.ts (RoutingMode) for the full explanation.
 *   direct — the endpoint's MITM proxy forwards straight to the real host.
 *   tunnel — the endpoint forwards through the firewall's own relay instead.
 * Default is 'direct'; tunnelling is opt-in and never implied by 'allow'.
 */
export type RoutingMode = 'direct' | 'tunnel';

export interface TargetGrant {
  id: string;
  orgId: string;
  deviceId: string;
  userUpn: string;
  providerId: string;
  providerLabel: string;
  grantedBy: string;
  grantedAt: number;
  expiresAt: number;
  expiresAtISO: string;
  revokedAt: number | null;
  revokedBy: string | null;
  reason: string | null;
  source: GrantSource;
  approvalId: string | null;
  extendedFrom: string | null;
  state: GrantState;
  msRemaining: number;
  routingMode: RoutingMode;
}

export interface EffectiveTarget {
  providerId: string;
  label: string;
  riskLevel?: string;
  type: 'llm' | 'mcp';
  hosts: string[];
  apps: string[];
  /** This device's standing rule. 'inherit' means the org default applies. */
  mode: TargetMode;
  /** What the org default says, so 'inherit' is legible rather than blank. */
  orgMode: TargetMode;
  /** This device's routing choice for an allow ('direct' unless set otherwise). */
  routing: RoutingMode;
  /** What the org default's routing says, so 'inherit' is legible. */
  orgRouting: RoutingMode;
  /** Per-user and per-AD-group rules; a group subject is `group:<SID>` with its synced `groupName`. */
  userRules: Array<{ userUpn: string; mode: TargetMode; routing: RoutingMode; groupName?: string }>;
  grants: TargetGrant[];
}

export interface TargetsResponse {
  deviceId: string;
  targets: EffectiveTarget[];
  now: number;
  maxGrantSecs: number;
}

export interface RuleInput {
  providerId: string;
  userUpn?: string;
  mode: TargetMode;
  /** Only meaningful when mode is 'allow'; the server ignores it otherwise. */
  routingMode?: RoutingMode;
}

async function asJson(res: Response): Promise<any> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || `Request failed (${res.status})`);
  return data;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

/** Device id to use for the org-wide defaults row. */
export const ORG_SCOPE = 'org';
/** Wildcard identity: a rule covering every user on the endpoint. */
export const ALL_USERS = '*';

export async function listTargets(apiUrl: string, deviceId: string): Promise<TargetsResponse> {
  return asJson(await fetch(`${apiUrl}/admin/bdr/devices/${encodeURIComponent(deviceId)}/targets`));
}

/**
 * Apply the given rules. Providers not named are left ALONE, not reset — two
 * admins with the same drawer open shouldn't silently undo each other's edits.
 */
export async function saveTargets(
  apiUrl: string,
  deviceId: string,
  rules: RuleInput[]
): Promise<TargetsResponse> {
  return asJson(
    await fetch(`${apiUrl}/admin/bdr/devices/${encodeURIComponent(deviceId)}/targets`, json('PUT', { rules }))
  );
}

export async function listGrants(
  apiUrl: string,
  opts: { deviceId?: string; includeExpired?: boolean } = {}
): Promise<{ grants: TargetGrant[]; now: number; maxGrantSecs: number }> {
  const q = new URLSearchParams();
  if (opts.deviceId != null) q.set('deviceId', opts.deviceId);
  if (opts.includeExpired) q.set('includeExpired', '1');
  const qs = q.toString();
  return asJson(await fetch(`${apiUrl}/admin/bdr/target-grants${qs ? `?${qs}` : ''}`));
}

export async function createGrant(
  apiUrl: string,
  input: {
    deviceId?: string;
    userUpn?: string;
    providerId: string;
    ttlSecs: number;
    reason?: string;
    /** Defaults to 'direct' server-side when omitted. */
    routingMode?: RoutingMode;
  }
): Promise<TargetGrant> {
  const data = await asJson(await fetch(`${apiUrl}/admin/bdr/target-grants`, json('POST', input)));
  return data.grant;
}

export async function extendGrant(apiUrl: string, id: string, addMs: number): Promise<TargetGrant> {
  const data = await asJson(
    await fetch(`${apiUrl}/admin/bdr/target-grants/${encodeURIComponent(id)}/extend`, json('POST', { addMs }))
  );
  return data.grant;
}

export async function revokeGrant(apiUrl: string, id: string, reason?: string): Promise<TargetGrant> {
  const data = await asJson(
    await fetch(`${apiUrl}/admin/bdr/target-grants/${encodeURIComponent(id)}`, json('DELETE', { reason }))
  );
  return data.grant;
}

/** 'jit' = time-bound grant, 'full' = standing allow for that user. */
export type AccessType = 'jit' | 'full';

/** A full-access rule minted by approving a request (org-wide, one user). */
export interface StandingAccess {
  userUpn: string;
  providerId: string;
  label: string;
  routingMode: RoutingMode;
  approvalId: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export async function listStandingAccess(apiUrl: string): Promise<StandingAccess[]> {
  const data = await asJson(await fetch(`${apiUrl}/admin/bdr/standing-access`));
  return data.rules || [];
}

export async function removeStandingAccess(apiUrl: string, userUpn: string, providerId: string): Promise<void> {
  await asJson(await fetch(`${apiUrl}/admin/bdr/standing-access`, json('DELETE', { userUpn, providerId })));
}

/** Duration presets for the grant form. */
export const DURATION_PRESETS: Array<{ labelKey: string; secs: number }> = [
  { labelKey: 'components.durationPresets.min30', secs: 1800 },
  { labelKey: 'components.durationPresets.hour1', secs: 3600 },
  { labelKey: 'components.durationPresets.hours4', secs: 14400 },
  { labelKey: 'components.durationPresets.hours8', secs: 28800 },
  { labelKey: 'components.durationPresets.day1', secs: 86400 },
  { labelKey: 'components.durationPresets.days3', secs: 259200 },
];

/**
 * Coarse countdown: "4h 12m", "38s". Deliberately imprecise above a minute —
 * this ticks once a second next to an absolute expiry timestamp, and a
 * seconds-accurate figure there invites reading the countdown as the contract
 * when the timestamp is the thing that was actually approved.
 */
export function remainingLabel(ms: number): string {
  if (ms <= 0) return 'expired';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}
